import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import RichText from '@/components/atoms/rich-text/rich-text';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Input from '@/components/molecules/input/input';
import useMutationAuthMagicLink from '@/integrations/services/auth-magic-link/auth-magic-link.hook';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useLoginContext } from '@/libraries/contexts/login-context';
import styles from './login-curtain-signup-step.module.scss';

export interface LoginCurtainSignupStepProps {
  buttonText?: string;
  /** The class name to apply */
  className?: string;
  /** The text to show as a headline */
  headline?: string;
  /** The text to show while loading */
  loadingText?: string;
  /** The text to show as a note */
  note?: string;
  /** Callback function to be called when the signup process is complete */
  onComplete?: () => void;
  /** Explicit redirect target for the magic link */
  redirectTo?: string;
  /** The text to show as a description */
  text?: string;
}

interface FormValues {
  email: string;
}

const LoginCurtainSignupStep = ({
  buttonText,
  className,
  headline,
  loadingText,
  note,
  onComplete,
  redirectTo,
  text,
}: LoginCurtainSignupStepProps) => {
  const { t } = useI18n();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { setEmail, setRetryTime } = useLoginContext();
  const { data, error, isError, isLoading, isSuccess, mutate } =
    useMutationAuthMagicLink();

  const getErrorMessage = () => {
    if (error && error.status === 429) {
      return t('items.errorHandling.magicLink.tooManyRequests');
    }

    return t('items.errorHandling.magicLink.genericError');
  };

  const onSubmit: SubmitHandler<FormValues> = data => {
    mutate({
      ...data,
      redirectTo:
        redirectTo ||
        (searchParams?.toString()
          ? `${pathname}?${searchParams.toString()}`
          : `${pathname}`),
    });
  };

  const { control, handleSubmit, register } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const watchedEmail = useWatch({ control, name: 'email' });

  useEffect(() => {
    if (!data || !isSuccess) return;

    setEmail(watchedEmail);
    setRetryTime(data.retryWaitTimeInSeconds);
    onComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccess, onComplete, watchedEmail]);

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isLoading]: isLoading,
      })}
    >
      <div className={styles.header}>
        <Typography
          className={styles.headline}
          tagStyle="headingMedium"
          text={headline}
        />

        {text && <RichText className={styles.text} text={text} />}
      </div>

      <form className={styles.main} onSubmit={handleSubmit(onSubmit)}>
        {isLoading && <Spinner loadingText={loadingText} />}

        <div className={styles.form}>
          <Input
            {...register('email')}
            error={isError ? getErrorMessage() : undefined}
            id="email"
            label={t('items.form.email')}
            required
          />

          <div className={styles.actions}>
            <Button text={buttonText} />

            {note && <RichText className={styles.note} text={note} />}
          </div>
        </div>
      </form>
    </div>
  );
};

LoginCurtainSignupStep.displayName = 'LoginCurtainSignupStep';

export default LoginCurtainSignupStep;
